import { DAY_IN_MILLISECONDS } from "shared/constants";
import store from "shared/services/localForageInstance";
import StorageService from "shared/services/StorageService";

const GLOBAL_ORGANISATION_ID = "global";

export const cacheOrganisationToken = (
  token,
  organisationId,
  adminScoped = false
) =>
  StorageService.set(
    tokenCacheKey(organisationId, adminScoped),
    JSON.stringify({
      token,
      validUntil: Date.now() + 30 * DAY_IN_MILLISECONDS,
    })
  );

function tokenCacheKey(organisationId, adminScoped = false) {
  return `token:${organisationId}${adminScoped ? ":admin" : ""}`;
}

function refreshTokenCacheKey(organisationId, adminScoped = false) {
  return `refreshToken:${organisationId}${adminScoped ? ":admin" : ""}`;
}

export const cacheOrganisationRefreshToken = (
  refreshToken,
  organisationId,
  adminScoped = false
) =>
  StorageService.set(
    refreshTokenCacheKey(organisationId, adminScoped),
    JSON.stringify({
      refreshToken,
      validUntil: Date.now() + 30 * DAY_IN_MILLISECONDS,
    })
  );

function expireCachedRefreshTokens() {
  const removalPromises = [];

  store.iterate((value, key) => {
    if (key.includes("refreshToken:")) {
      try {
        const expiry = JSON.parse(value)?.validUntil;

        if (expiry && expiry < Date.now()) {
          removalPromises.push(StorageService.remove(key, store));
        }
      } catch {
        // do nothing
      }
    }
  });

  return Promise.allSettled(removalPromises);
}

function expireCachedTokens() {
  const removalPromises = [];

  store.iterate((value, key) => {
    if (key.includes("token:")) {
      try {
        const expiry = JSON.parse(value)?.validUntil;

        if (expiry && expiry < Date.now()) {
          removalPromises.push(StorageService.remove(key, store));
        }
      } catch {
        // do nothing
      }
    }
  });

  return Promise.allSettled(removalPromises);
}

async function setCachedRefreshToken(refreshToken, organisationId) {
  const organisationKey = organisationId || getActiveOrganisationId();
  const adminScoped = await isAdminUser();

  if (!adminScoped && organisationKey !== GLOBAL_ORGANISATION_ID) {
    await StorageService.set("organisationId", organisationKey);
  }

  if (organisationKey !== GLOBAL_ORGANISATION_ID) {
    await StorageService.set("organisationId", organisationKey, sessionStorage);
  }

  await cacheOrganisationRefreshToken(
    refreshToken,
    organisationKey,
    adminScoped
  );
}

async function setCachedToken(token, organisationId) {
  const organisationKey = organisationId || getActiveOrganisationId();
  const adminScoped = await isAdminUser();

  if (!adminScoped && organisationKey !== GLOBAL_ORGANISATION_ID) {
    await StorageService.set("organisationId", organisationKey);
  }

  if (organisationKey !== GLOBAL_ORGANISATION_ID) {
    await StorageService.set("organisationId", organisationKey, sessionStorage);
  }

  await cacheOrganisationToken(token, organisationKey, adminScoped);
}

export const getActiveOrganisationId = () =>
  sessionStorage.getItem("organisationId") || GLOBAL_ORGANISATION_ID;

export const isAdminUser = async () => {
  try {
    const accountManager =
      JSON.parse(await StorageService.get("accountManager")) || {};

    return Boolean(Object.keys(accountManager).length);
  } catch {
    return false;
  }
};

export const cachedToken = async (organisationId) => {
  try {
    const adminScoped = await isAdminUser();

    const { token, validUntil } =
      JSON.parse(
        await StorageService.get(tokenCacheKey(organisationId, adminScoped))
      ) || {};

    if (!token || !validUntil) return null;

    if (validUntil <= Date.now()) return null;

    return token;
  } catch {
    return null;
  }
};

export const cachedRefreshToken = async (organisationId) => {
  try {
    const adminScoped = await isAdminUser();

    const { refreshToken, validUntil } =
      JSON.parse(
        await StorageService.get(
          refreshTokenCacheKey(organisationId, adminScoped)
        )
      ) || {};

    if (!refreshToken || !validUntil) return null;

    if (validUntil <= Date.now()) return null;

    return refreshToken;
  } catch {
    return null;
  }
};

export const clearCachedTokens = async () => {
  const removalPromises = [];

  await store.iterate((_value, key) => {
    if (key.includes("token:")) {
      removalPromises.push(StorageService.remove(key, store));
    }
  });

  return Promise.allSettled(removalPromises);
};

export const clearCachedRefreshTokens = async () => {
  const removalPromises = [];

  await store.iterate((_value, key) => {
    if (key.includes("refreshToken:")) {
      removalPromises.push(StorageService.remove(key, store));
    }
  });

  return Promise.allSettled(removalPromises);
};

export const clearCachedAuthTokens = () =>
  Promise.all([clearCachedTokens(), clearCachedRefreshTokens()]);

export const updateUserToken = async (token, organisationId) => {
  if (!token) return;

  expireCachedTokens();

  await setCachedToken(token, organisationId);
};

export const updateUserRefreshToken = async (refreshToken, organisationId) => {
  if (!refreshToken) return;

  expireCachedRefreshTokens();

  await setCachedRefreshToken(refreshToken, organisationId);
};

export const currentToken = () => cachedToken(getActiveOrganisationId());

export const currentRefreshToken = () =>
  cachedRefreshToken(getActiveOrganisationId());

export const loadOrganisationToken = async () => {
  const organisationId = await StorageService.get("newOrgId", sessionStorage);

  if (organisationId) {
    // further updates needed here for when we do multi-org switching via identity for refresh_token
    const orgToken = await cachedToken(organisationId);

    if (!orgToken) {
      StorageService.remove("newOrgId", sessionStorage);

      return;
    }

    await Promise.all([
      updateUserToken(orgToken, organisationId),
      StorageService.remove("newOrgId", sessionStorage),
    ]);
  }
};

export default {
  cachedRefreshToken,
  cachedToken,
  cacheOrganisationToken,
  clearCachedAuthTokens,
  clearCachedRefreshTokens,
  clearCachedTokens,
  currentRefreshToken,
  currentToken,
  getActiveOrganisationId,
  isAdminUser,
  loadOrganisationToken,
  updateUserToken,
};
